import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	ReactNode,
	useCallback,
} from "react";
import { useLocation } from "@reach/router";
import { DisplayRfx, Rfx } from "../types/rfx.types";
import { isRfxOnboardingType } from "../utils";
import { getRfxById } from "../api/rfx";
import { RfxTabTotals } from "../types/component-types";
import { getTabCounts } from "../Submission/components/ViewSubmission/useRfxTabMenu";
import { useIsReadOnlyMode } from "newui/features/ExecutiveView";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useRfxNavigate } from "app/useRfxNavigate";
import {
	extractRfxForbiddenReason,
	getRfxForbiddenRedirectPath,
} from "../utils/rfx-forbidden-redirect";
import { getEvaluationState } from "../Evaluation/api/evaluation-api";
import { getRfxReadOnlyPreview } from "../hooks/useRfxReadOnlyPreview";
import type { RfxReadOnlyPreviewResource } from "../types/rfx-read-only-preview.type";

/**
 * TASK-028: this provider is the shared loader for every procurer
 * `edit-rfx`/`view-rfx`/`evaluation` route. The Evaluation subtree is the one
 * consumer that must NOT bounce a cross-company panel member away on a
 * procurer-detail 403 — panel access to Evaluation is legitimate and is
 * decided by the evaluation-status endpoint itself (`EvaluationAccessBoundary`
 * already renders the friendly VT-043/044-contract denial for genuinely
 * unentitled viewers there). Detected structurally off the URL rather than a
 * prop so every mount point (Account/Supplier/Guest route trees) is covered
 * without touching those route files.
 */
const isEvaluationPathname = (pathname: string | undefined): boolean =>
	/\/evaluation(\/|$)/.test(pathname ?? "");

/**
 * Builds a display-only, TS-shape-conformant `Rfx` from the panel-sanctioned
 * read-only preview resource (TASK-028). The preview surface intentionally
 * carries none of the procurer-only fields (`companyOwner`, `rfxType`,
 * `supplierList`, `rfxLead`, `project`, etc. — see TASK_SUMMARY-004) — those
 * are left at safe empty defaults below. Every Evaluation-tree consumer of
 * `rfxData` optional-chains every field it reads (grep-verified for this
 * task), so the empty defaults are never rendered; they exist only so this
 * object satisfies `Rfx`'s TypeScript shape for consumers still typed
 * against the full procurer contract. If a future Evaluation surface needs a
 * real value here (e.g. `rfxLead` for the lead badge), that field must be
 * added to `RfxReadOnlyPreviewResource` server-side — do not fake it here.
 */
const buildDisplayRfxFromPreview = (
	preview: RfxReadOnlyPreviewResource
): Rfx =>
	({
		id: preview.id,
		uuid: preview.uuid,
		name: preview.name ?? "",
		description: preview.description ?? "",
		client: preview.client ?? "",
		location: preview.location ?? "",
		rfqIdNumber: preview.rfx_id_number ?? "",
		submissionDeadline: preview.submission_deadline ?? "",
		submissionTimezone: preview.submission_timezone ?? "",
		type: preview.type ?? "",
		documentationDescription: preview.documentation_description ?? "",
		documentationDocument: [],
		addendum_count: preview.addenda?.length ?? 0,
		comms_count: 0,
		clarifications_count: 0,
	} as unknown as Rfx);

interface RfxDataContextType {
	hasRfxLoaded: boolean;
	isOnboarding: boolean;
	mode: "api" | "create-preview"; // api is default retrieval from API, create-preview allows you to set own data for preview only
	rfxData: DisplayRfx | null;
	rfxId: number | null;
	rfxTabTotals: RfxTabTotals;

	// Switched-executive "View as RFx Lead" override (read-only only). Lets a
	// switched executive reveal the lead-gated evaluation UI for viewing. It is
	// a VISIBILITY flag only — it never enables writes — and is force-reset to
	// false whenever executive view is not active so it cannot leak into a
	// normal session.
	viewAsRfxLead: boolean;

	// Setters (rarely needed)
	reloadRfxData: () => void;
	setPreviewData: (data: Rfx | null) => void;
	setRfxTabTotals: (tabTotals: RfxTabTotals) => void;
	setViewAsRfxLead: (value: boolean) => void;
	updateLocalRfxData: (newRfxData: Rfx | null) => void;
}

const RfxDataContext = createContext<RfxDataContextType | null>(null);

export function RfxDataProvider({
	rfxRouteId,
	children,
}: {
	children: ReactNode;
	rfxRouteId: number;
}) {
	const { state, pathname } = useLocation() as any;
	const isReadOnly = useIsReadOnlyMode();
	const { clientPaths } = useNavigationPaths();
	const rfxNavigate = useRfxNavigate();
	// const params = useParams<{ rfxId?: string }>();

	// TASK-028: see `isEvaluationPathname` docblock above.
	const isEvaluationRoute = isEvaluationPathname(pathname);
	const [triedEvaluationFallback, setTriedEvaluationFallback] =
		useState<number | null>(null);

	// TASK-014 AC3: a panel member landing on a procurer-only URL (e.g. this
	// provider mounted under an edit-rfx/view-rfx route they don't have
	// procurer access to) gets a friendly redirect to their permitted view,
	// driven entirely by the server's `RfxForbiddenReason` — never a bare 403
	// page, never an FE heuristic.
	const [forbiddenReason, setForbiddenReason] = useState<
		ReturnType<typeof extractRfxForbiddenReason>
	>(undefined);

	/**
	 * URL route ID takes priority unless preview mode is active
	 */
	const apiId = rfxRouteId ? Number(rfxRouteId) : null;

	/**
	 * Optional preview data passed via router state
	 * navigate("/preview", { state: { previewData } })
	 */
	const previewDataFromRoute: Rfx | null = state?.previewData ?? null;

	const [previewData, setPreviewData] = useState<Rfx | null>(
		previewDataFromRoute
	);

	const [rfxData, setRfx] = useState<DisplayRfx | null>(null);
	const [rfxId, setRfxId] = useState<number | null>(apiId);
	const [hasFailedToLoadId, setHasFailedToLoadId] = useState<number | null>();
	const [isLoadingRfx, setIsLoadingRfx] = useState<boolean>(false);
	const [loadedRfx, setLoadedRfx] = useState<number>(0);
	const [rfxTabTotals, setRfxTabTotals] = useState<RfxTabTotals>(
		getTabCounts(
			rfxData,
			rfxData?.questionnaire?.length,
			rfxData?.quotes?.length
		)
	);

	// Switched-executive "View as RFx Lead" override (read-only visibility only).
	const [viewAsRfxLead, setViewAsRfxLead] = useState<boolean>(false);

	const mode: "api" | "create-preview" = previewData ? "create-preview" : "api";

	// Never let the override leak into a normal session: whenever executive view
	// is not active, force it back to false.
	useEffect(() => {
		if (!isReadOnly && viewAsRfxLead) {
			setViewAsRfxLead(false);
		}
	}, [isReadOnly, viewAsRfxLead]);

	const generateDisplayData = (rfxData: Rfx) => {
		if (!rfxData) {
			return rfxData as DisplayRfx;
		}

		return {
			...rfxData,
			displayName: rfxData.project?.name + " | " + rfxData.supplierList?.name,
		} as DisplayRfx;
	};

	const reloadRfxData = useCallback(() => {
		setLoadedRfx(0);
	}, []);

	const updateLocalRfxData = useCallback((rfxData: Rfx) => {
		setLoadedRfx(rfxData?.id ?? 0);
		setRfx(generateDisplayData(rfxData));
		setRfxId(rfxData?.id);
		setRfxTabTotals({
			addendums: rfxData?.addendum_count ?? 0,
			clarifications: rfxData?.clarifications_count ?? 0,
			comms: rfxData?.comms_count ?? 0,
			documentation: rfxData?.documentationDocument?.length ?? 0,
			evaluation: 0,
			quote: 1, // TODO: need to check how this is calculated
			returnableSchedule: 1, // TODO: need to check how this is calculated
		});
	}, []);

	useEffect(() => {
		if (mode === "create-preview" && previewData) {
			setRfx(generateDisplayData(previewData));
			setRfxId(previewData?.id ?? null);
		}
	}, [mode, previewData]);

	// ────────────────────────────────────────────
	// LOAD FROM API MODE (view/edit/preview)
	// ────────────────────────────────────────────
	useEffect(() => {
		if (
			!rfxId ||
			!apiId ||
			isLoadingRfx ||
			loadedRfx === rfxId ||
			mode === "create-preview" ||
			hasFailedToLoadId === rfxId
		) {
			return;
		}

		setIsLoadingRfx(true);

		// TASK-028: on the Evaluation route this numeric-id call is expected to
		// 403 for cross-company/panel-only members — that is not an error, it's
		// the trigger for the evaluation-status fallback above, which already
		// surfaces the correct panel-sanctioned data (or `EvaluationAccessBoundary`'s
		// own friendly denial if genuinely unentitled). Suppress the generic
		// "unauthorized" toast there so the fallback isn't accompanied by a
		// stray error toast; every other route keeps the toast.
		getRfxById(
			rfxId,
			null,
			(err) => {
				const reason = extractRfxForbiddenReason(err);
				if (reason) {
					setForbiddenReason(reason);
				}
			},
			isEvaluationRoute
		)
			.then((rfxData) => {
				if (!rfxData) {
					// TODO: need do something nice for user here
					setHasFailedToLoadId(rfxId);
					return;
				}

				updateLocalRfxData(rfxData);
			})
			.finally(() => setIsLoadingRfx(false));
	}, [
		mode,
		apiId,
		hasFailedToLoadId,
		isLoadingRfx,
		isEvaluationRoute,
		rfxId,
		loadedRfx,
		updateLocalRfxData,
	]);

	// TASK-028: on the Evaluation route, a procurer-detail 403 does not mean
	// "unentitled" — a cross-company panel member is expected to 403 here
	// (VT-044) but IS entitled to the evaluation itself, which is decided by
	// the panel-sanctioned evaluation-status endpoint, not this one. Attempt
	// to resolve display data from that sanctioned source (+ the uuid-only
	// read-only preview) instead of the TASK-014 forbidden-redirect below. If
	// the evaluation-status call *also* denies (a genuine stranger), we
	// deliberately do nothing further here — `EvaluationAccessBoundary`
	// (inside `EvaluationPage`) independently calls that same endpoint and
	// already renders the correct friendly denial (VT-043/044 contract), so
	// there is no gap and no redirect loop.
	useEffect(() => {
		if (
			!isEvaluationRoute ||
			!forbiddenReason ||
			!rfxId ||
			triedEvaluationFallback === rfxId
		) {
			return;
		}

		setTriedEvaluationFallback(rfxId);

		getEvaluationState(rfxId)
			.then((evaluationState) => {
				// TASK-029b: the backend now returns a distinct `rfx_uuid` on the
				// evaluation-status payload (mapped to `EvaluationState.rfxUuid`),
				// panel-sanctioned like the rest of this endpoint, so use that here
				// rather than `evaluationState.uuid` (the evaluation's own uuid,
				// which 403s against `/rfq/{uuid}/preview`). Toast is suppressed
				// below in case this genuinely-unentitled viewer still 403s.
				const uuid = evaluationState?.rfxUuid;
				if (!uuid) {
					return null;
				}
				return getRfxReadOnlyPreview(uuid, true);
			})
			.then((preview) => {
				if (preview) {
					updateLocalRfxData(buildDisplayRfxFromPreview(preview));
				}
			})
			.catch(() => {
				// Genuinely unentitled (or transient failure) — leave to
				// EvaluationAccessBoundary's own denial handling.
			});
	}, [isEvaluationRoute, forbiddenReason, rfxId, triedEvaluationFallback, updateLocalRfxData]);

	// Friendly redirect (TASK-014 AC3): fire once we both have a reason and
	// somewhere to send the user — `clientPaths` is null until the current
	// company resolves, so this can trail the 403 by a tick, which is fine
	// (nothing procurer-only renders in the meantime; `hasFailedToLoadId` is
	// already set). Never fires on the Evaluation route (TASK-028) — panel
	// members belong there, so `EvaluationAccessBoundary` owns denial instead.
	useEffect(() => {
		if (!forbiddenReason || !clientPaths || isEvaluationRoute) {
			return;
		}

		rfxNavigate(getRfxForbiddenRedirectPath(forbiddenReason, clientPaths), {
			replace: true,
		});
	}, [forbiddenReason, clientPaths, rfxNavigate, isEvaluationRoute]);

	// ────────────────────────────────────────────
	// 3. Derived state
	// ────────────────────────────────────────────
	const isOnboarding = useMemo(() => {
		if (!rfxData) return false;
		return isRfxOnboardingType(rfxData.rfxType || rfxData.type);
	}, [rfxData]);

	// Memoise the context value so its identity only changes when the underlying
	// data changes — not on every render. An unmemoised value object churned the
	// identity of consumers' callbacks (e.g. RfxSubmissionDataProvider's
	// updateLocalRfxData depends on this object), re-firing their effects.
	const value = useMemo<RfxDataContextType>(
		() => ({
			mode,
			rfxData,
			rfxId,
			hasRfxLoaded: loadedRfx !== 0,
			isOnboarding,
			rfxTabTotals,
			viewAsRfxLead,
			reloadRfxData,
			setPreviewData,
			setRfxTabTotals,
			setViewAsRfxLead,
			updateLocalRfxData,
		}),
		[
			mode,
			rfxData,
			rfxId,
			loadedRfx,
			isOnboarding,
			rfxTabTotals,
			viewAsRfxLead,
			reloadRfxData,
			setPreviewData,
			setRfxTabTotals,
			setViewAsRfxLead,
			updateLocalRfxData,
		]
	);

	return (
		<RfxDataContext.Provider value={value}>{children}</RfxDataContext.Provider>
	);
}

export function useRfxData(
	ignoreContextCheck = false
): RfxDataContextType | null {
	const context = useContext(RfxDataContext);
	if (!ignoreContextCheck && !context) {
		throw new Error("useRfxData must be used inside <RfxDataProvider>");
	}
	return context;
}
